import { useState, useEffect } from 'react';
import { DEFAULT_LANGUAGE } from '../constants/GeneralUserInfo';
import ResponseStatus from '../constants/ResponseStatus';
import { showErrorNotificationFromResponse } from './errorUtils';
import useMessageLanguageByRoute from './useMessageLanguageByRoute';
import { getLanguages, getLocales, getTimezones } from '../veevaIdApi';
import adaptLocaleOptionsData from './adaptLocaleOptionsData';
import getStaticLanguages from './getStaticLanguages';

/**
 * @typedef ErrorResponse
 * @property {string} id
 * @property {string} type
 * @property {string} timestamp
 */

/**
 * Concatenates at most three error ids into one error response
 * @param responses list of responses from fetching languages, locales and timezones
 * @returns {ErrorResponse}
 */
const mergeErrorResponseIds = (responses) => {
    const mergedErrorResponse = {};
    responses.forEach((response) => {
        if (!mergedErrorResponse.responseStatus) {
            mergedErrorResponse.responseStatus = response.value.responseStatus;
        }
        if (mergedErrorResponse.errors && response.value.errors) {
            mergedErrorResponse.errors[0].id = `${mergedErrorResponse.errors[0].id}, ${response.value.errors[0].id}`;
        } else if (!mergedErrorResponse.errors) {
            mergedErrorResponse.errors = response.value.errors;
        }
    });
    return mergedErrorResponse;
};

const hasErrors = (responses) => {
    return responses.some((response) => response.value.responseStatus === ResponseStatus.FAILURE);
};

const intialState = {
    languages: {},
    locales: {},
    timezones: [],
};

/**
 * Makes request for language, locale and timezone options.
 * - adapts server results for each picker component
 * - returns server results for error handling in the calling component
 *
 * @param {string} language
 */
const useLanguagesLocalesTimezones = (language) => {
    const [allOptions, setAllOptions] = useState(intialState);
    const [allResponses, setAllResponses] = useState({});
    const messageLanguage = useMessageLanguageByRoute();

    const setLocaleOptions = async (lang) => {
        if (!lang) {
            setAllOptions({ ...allOptions, locales: {} });
            return;
        }
        const response = await getLocales(lang);
        const updatedLocales = await adaptLocaleOptionsData(
            response.data.locales,
            messageLanguage, // translate locale options based on the messageLanguage, not by user lang
        );
        const updatedOptions = { ...allOptions, locales: updatedLocales };
        setAllOptions(updatedOptions);
        setAllResponses({ localesResponse: response });
    };

    useEffect(() => {
        (async () => {
            const promises = [getLocales, getLanguages, getTimezones].map((callback, i) => {
                return new Promise((resolve) => {
                    // remove passing the language when the language/locale decoupling feature flag is removed
                    resolve(callback(language || messageLanguage || DEFAULT_LANGUAGE));
                });
            });
            const response = await Promise.allSettled(promises);

            if (hasErrors(response)) {
                const errorResponses = mergeErrorResponseIds(response);
                const i18nOptions = { language };
                await showErrorNotificationFromResponse(errorResponses, i18nOptions);
                return;
            }
            const localesResponse = response[0].value;
            const locales = await adaptLocaleOptionsData(
                localesResponse.data.locales,
                messageLanguage,
            );

            const languagesResponse = response[1].value;
            const languages = getStaticLanguages(languagesResponse.data.languages);

            const timezonesResponse = response[2].value;
            const timezones = timezonesResponse.data.timezones;

            setAllOptions({ languages, locales, timezones });
            setAllResponses({ localesResponse }); // add language and timezone responses when needed
        })();
    }, [messageLanguage, language]);

    return [allOptions, allResponses, setLocaleOptions];
};

export default useLanguagesLocalesTimezones;
